// File requests: new legal aid files, from request to decision.
//
// LACW's and VALS's new-file emails are collected by the Render service (/api/lacw-emails), and staff
// add others by hand (vals_drafts, form_type "manual_email_request"). Staff move each one
// through lodging in ATLAS to a decision. This used to live only in the separate Legal Aid
// Status Monitor; the Aid Planner now shows the same requests beside the matters they
// become, and reading a letter moves its request on.
//
// The shapes here are the ones the Status Monitor has always used (it was a stand-alone page,
// public/lacw-portal-tool, until October 2026; it is now src/pages/LegalAidStatusMonitor.jsx),
// so both it and the Aid Planner show and change the same records.

import { nameKeys } from "./diaryAidMatch.js";

export const FILE_REQUEST_STATUSES = [
  { value: "", label: "New file request" },
  { value: "waiting_to_be_submitted_in_atlas", label: "Waiting to be lodged" },
  { value: "pending_in_atlas", label: "Pending in ATLAS" },
  { value: "aid_submitted", label: "Aid submitted" },
  { value: "aid_approved", label: "Aid approved" },
  { value: "aid_not_submitted", label: "Aid not submitted" },
];

// Older values the Status Monitor still reads.
const LEGACY = { completed: "aid_submitted", not_completed: "aid_not_submitted", pending: "pending_in_atlas" };

export function normaliseRequestStatus(status = "") {
  const value = String(status || "");
  return LEGACY[value] || (FILE_REQUEST_STATUSES.some((option) => option.value === value) ? value : "");
}

export function requestStatusLabel(status) {
  return FILE_REQUEST_STATUSES.find((option) => option.value === normaliseRequestStatus(status))?.label || "New file request";
}

// The client, from a request email's subject: "Fwd: New file: Marie Jones vs Police (Heidelberg)"
// is Marie Jones. The Status Monitor's own rule.
export function clientNameFromSubject(subject = "") {
  if (!subject) return "Unknown client";
  let name = String(subject).replace(/^(?:fw|fwd|re|aw|wg)\s*:\s*/i, "");
  name = name.replace(/^new\s*[-_]?\s*(?:manual\s+)?file\s*[:\u2013-]\s*/i, "");
  name = name.split(/\b(?:vs|v\.)\b/i)[0];
  name = name.split(/\s+in\s+/i)[0];
  name = name.split(/\s+-\s+/)[0];
  name = name.replace(/\(.*\)/g, "");
  return name.trim() || "Unknown client";
}

// One request per email thread (Emma, 21 Oct 2026). A firm's "thanks", "here is the
// document again" and other replies in the thread are the same request, not new ones: EAS's
// one new matter for Al Housine Hilmi was listed three times. A later email in the thread
// that is a different kind of request - an extension, a grant status check, which changes
// the grant - stays a request of its own.
//
// The request is the thread's first email. It shows the newest status and notes any email
// in the thread carries (each email kept its own, so they had drifted apart), and a change
// is saved to every email in it (threadMessageIds), so they agree from then on. The later
// emails are kept as followUps, to be read from the request.
const emailDate = (email) => String(email?.date || email?.created_at || "");

export function groupEmailThreads(emails = []) {
  const groups = new Map();
  for (const email of Array.isArray(emails) ? emails : []) {
    if (!email?.message_id) continue;
    const key = `${email.thread_id || email.message_id}|${email.request_category || ""}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(email);
  }
  return [...groups.values()].map((group) => group.sort((a, b) => emailDate(a).localeCompare(emailDate(b))));
}

const newestWith = (group, field) =>
  [...group].reverse().find((email) => String(email[field] || "").trim())?.[field] || "";

// Both sources as one list, newest first.
export function normaliseFileRequests(emails = [], drafts = []) {
  const fromEmail = groupEmailThreads(emails).map((group) => {
    const [email, ...later] = group;
    return {
      key: `email:${email.message_id}`,
      messageId: email.message_id,
      threadMessageIds: group.map((each) => each.message_id),
      followUps: later.map((each) => ({
        messageId: each.message_id,
        subject: each.subject || "",
        snippet: each.snippet || "",
        date: emailDate(each),
      })),
      isManual: false,
      subject: email.subject || "",
      snippet: email.snippet || "",
      date: emailDate(email),
      status: normaliseRequestStatus(newestWith(group, "status")),
      // When the status last changed (Deadline Guard stamps it, from 21 Oct 2026). Every
      // email in a thread is changed together, so the latest stamp is the request's.
      statusUpdatedAt: group.map((each) => String(each.status_updated_at || "")).sort().pop() || "",
      notes: newestWith(group, "notes"),
      // From the Render service's subject classifier: New File, Grant Status Check, ...
      category: email.request_category_label || "",
      urgent: group.some((each) => Boolean(each.is_urgent)),
      raw: email,
    };
  });
  const fromDrafts = (Array.isArray(drafts) ? drafts : [])
    .filter((draft) => draft?.form_type === "manual_email_request" && draft.form_data)
    .map((draft) => ({
      key: `manual:${draft.id}`,
      messageId: draft.form_data.message_id || draft.id,
      draftId: draft.id,
      draftName: draft.draft_name || "",
      isManual: true,
      subject: draft.form_data.subject || "",
      snippet: draft.form_data.snippet || "",
      date: draft.form_data.date || draft.created_at || "",
      status: normaliseRequestStatus(draft.form_data.status),
      statusUpdatedAt: draft.form_data.statusUpdatedAt || "",
      notes: draft.form_data.notes || "",
      // Requests raised by a tool (a travel claim's flights) say what they are and who for.
      category: draft.form_data.category || "",
      clientName: draft.form_data.client_name || "",
      raw: draft,
    }));
  return [...fromDrafts, ...fromEmail]
    .map((request) => ({ ...request, clientName: request.clientName || clientNameFromSubject(request.subject) }))
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
}

function matterName(matter) {
  return matter?.client?.fullName || [matter?.client?.firstName, matter?.client?.lastName].filter(Boolean).join(" ");
}

function sameName(a, b) {
  const keys = nameKeys(a);
  return keys.size > 0 && [...nameKeys(b)].some((key) => keys.has(key));
}

// The Aid Planner matter a request became, if exactly one has the client's name.
export function matterForRequest(request, matters = []) {
  const found = matters.filter((matter) => matter?.matterId && sameName(request.clientName, matterName(matter)));
  return found.length === 1 ? found[0] : null;
}

// Reading a letter moves its request on: an application request letter means it has been
// lodged, an approval letter that aid is approved. Returns the requests to change, and to
// what; never moves a request backwards or off a final decision.
const ORDER = ["", "waiting_to_be_submitted_in_atlas", "pending_in_atlas", "aid_submitted", "aid_approved"];

export function requestUpdatesForLetter(requests = [], { documentType, clientName, extensionNumber } = {}) {
  let status = "";
  if (documentType === "grant_approval_letter" && !(Number(extensionNumber) > 0)) status = "aid_approved";
  else if (documentType === "application_request" && !(Number(extensionNumber) > 0)) status = "aid_submitted";
  if (!status || !clientName) return [];
  return requests
    .filter((request) => sameName(request.clientName, clientName))
    .filter((request) => request.status !== "aid_not_submitted")
    .filter((request) => ORDER.indexOf(request.status) < ORDER.indexOf(status))
    .map((request) => ({ request, status }));
}

// A request added by hand, in the shape the Status Monitor reads. draft_name is required by
// /api/vals-drafts; the Status Monitor's own form left it out, so its hand-added requests
// were refused while it reported success.
export function manualRequestDraft({ clientName, aidType = "", dateLodged = "", status = "", notes = "" }, brand = "lacw", now = new Date()) {
  const id = `manual_${now.getTime()}`;
  const lodged = dateLodged ? new Date(dateLodged) : null;
  const detail = `Aid type: ${aidType || "not given"}${lodged ? ` • Date lodged: ${lodged.toLocaleDateString("en-AU")}` : ""}`;
  return {
    id,
    form_type: "manual_email_request",
    draft_name: `File request - ${clientName}`,
    brand,
    form_data: {
      message_id: id,
      subject: `New manual file: ${clientName}${aidType ? ` - ${aidType}` : ""}`,
      snippet: detail,
      body: detail,
      from_address: "manual@completelawsupport.com",
      date: (lodged || now).toISOString(),
      status: normaliseRequestStatus(status),
      notes,
      client_id: brand,
      is_manual: true,
    },
  };
}

// --- The Legal Aid Status Monitor page (src/pages/LegalAidStatusMonitor.jsx) ---------------

// Status Monitor filter tabs: every status, plus "open" (not yet decided) and "all".
export const CLOSED_REQUEST_STATUSES = ["aid_approved", "aid_not_submitted"];

// The kinds of request email, as the Render service's subject classifier names them, in the
// order the Client Account dashboard showed them. Any other type it sends is listed after.
const REQUEST_TYPE_ORDER = ["New File", "Grant Status Check", "Extension App", "Disbursement - To pay", "Disbursement - Check"];
export const NO_REQUEST_TYPE = "No type";

const requestType = (request) => String(request?.category || "").trim() || NO_REQUEST_TYPE;

// The types present, each with how many requests it has, for the type filter.
export function requestTypeOptions(requests = []) {
  const counts = new Map();
  for (const request of requests) counts.set(requestType(request), (counts.get(requestType(request)) || 0) + 1);
  const rank = (type) => {
    const index = REQUEST_TYPE_ORDER.indexOf(type);
    return index === -1 ? (type === NO_REQUEST_TYPE ? 99 : 50) : index;
  };
  return [...counts]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => rank(a.value) - rank(b.value) || a.value.localeCompare(b.value));
}

export function matchesRequestType(request, type = "all") {
  return type === "all" || requestType(request) === type;
}

/** Requests grouped by the month they arrived, newest month first. */
export function groupRequestsByMonth(requests = []) {
  const groups = new Map();
  for (const request of requests) {
    const date = request.date ? new Date(request.date) : null;
    const valid = date && !Number.isNaN(date.getTime());
    const key = valid ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}` : "unknown";
    const label = valid ? date.toLocaleDateString("en-AU", { month: "long", year: "numeric" }) : "Date unknown";
    if (!groups.has(key)) groups.set(key, { key, label, requests: [] });
    groups.get(key).requests.push(request);
  }
  return [...groups.values()].sort((a, b) => (a.key === "unknown" ? 1 : b.key === "unknown" ? -1 : b.key.localeCompare(a.key)));
}

// Attachments are kept in a request's notes as markdown links, "[Approval Letter: x.pdf](/api/files?path=...)".
// Only the portal's own file links and web addresses count; anything else (a javascript:
// address typed into a note) stays as plain text.
const NOTE_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const SAFE_LINK = /^(?:\/api\/files\?|https?:\/\/)/i;

/** The note split into its text and its attachment links. */
export function splitRequestNotes(notes = "") {
  const attachments = [];
  const text = String(notes || "").replace(NOTE_LINK, (match, label, url) => {
    if (!SAFE_LINK.test(url)) return match;
    attachments.push({ label, url });
    return "";
  }).replace(/\n{3,}/g, "\n\n").trim();
  return { text, attachments };
}

/** Put the text back together with the attachment links, so editing the text keeps them. */
export function joinRequestNotes(text = "", attachments = []) {
  const links = attachments.map((a) => `[${a.label}](${a.url})`);
  return [String(text || "").trim(), ...links].filter(Boolean).join("\n\n");
}

/** The letter this request is waiting for, or "" - as the Status Monitor always offered. */
export function letterToAttach(request) {
  const notes = String(request?.notes || "");
  if (["aid_submitted", "pending_in_atlas"].includes(request?.status) && !notes.includes("Submission Letter")) return "Submission Letter";
  if (request?.status === "aid_approved" && !notes.includes("Approval Letter")) return "Approval Letter";
  return "";
}

/**
 * What to send the Render service when a request changes. It saves status and notes together
 * and clears whichever is missing, so both always go: the change, and the other as it stands.
 */
export function emailRequestUpdate(request = {}, changes = {}) {
  return {
    status: normaliseRequestStatus(changes.status !== undefined ? changes.status : request.status),
    notes: String((changes.notes !== undefined ? changes.notes : request.notes) || ""),
  };
}

// Email text as saved by the Render service: tags already removed, but entities and runs of
// blank lines left in. Shown as plain text, never as HTML.
const ENTITIES = { "&nbsp;": " ", "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": "\"", "&#39;": "'", "&#039;": "'" };
export function cleanEmailText(text = "") {
  return String(text || "")
    .replace(/&(?:nbsp|amp|lt|gt|quot|#39|#039);/g, (entity) => ENTITIES[entity])
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** A file size people can read: 980 KB, 2.4 MB. */
export function readableSize(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
}

// What actually gets sent when a request is added by hand.
//
// The same as the draft, minus its id. The draft's id is "manual_<timestamp>", which
// identifies the request inside form_data; vals_drafts.id is a uuid column, so sending it
// as the row id made Postgres refuse every insert. Add a request failed against the real
// database while appearing to work in LACW test mode, which keeps drafts in this browser
// where any string is a usable id.
//
// The database generates the row id. The manual id stays in form_data.message_id, which is
// what the planner matches a manual request on.
export function manualRequestPayload(details, brand = "lacw", now = new Date()) {
  const { id, ...payload } = manualRequestDraft(details, brand, now);
  return payload;
}
